import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useSyncExternalStore } from 'react'

import type { Storage } from '@/lib/storage'
import storage from '@/lib/storage'

/**
 * A hook to interface with the local storage
 * @param key The key to manage
 * @returns A tuple containing the value, a setter and a remover
 * @example const [value, set, remove] = useStorage('key')
 */
export const useStorage = <T extends keyof Storage>(
  key: T,
): [Storage[T], Dispatch<SetStateAction<Storage[T]>>, () => void] => {
  // Subscribe to storage changes
  const subscribe = useCallback(
    (onStorageChange: () => void) => {
      const handler = (event: StorageEvent) => {
        if (event.storageArea !== localStorage) return
        if (event.key !== key) return
        onStorageChange()
      }

      window.addEventListener('storage', handler)
      return () => {
        window.removeEventListener('storage', handler)
      }
    },
    [key],
  )

  // Get the current value from the storage
  const getSnapshot = useCallback(() => storage.get(key), [key])
  const getServerSnapshot = useCallback(() => storage.get(key), [key])

  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  // Setter function (supports functional updates)
  const setValue: Dispatch<SetStateAction<Storage[T]>> = useCallback(
    next => {
      const prev = storage.get(key)
      const value = typeof next === 'function' ? next(prev) : next
      storage.set(key, value)

      // Manually dispatch a storage event to notify other hooks in the same tab
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          oldValue: JSON.stringify(prev),
          newValue: JSON.stringify(value),
          storageArea: localStorage,
        }),
      )
    },
    [key],
  )

  // Remover function
  const remove = useCallback(() => {
    const prev = storage.get(key)
    storage.remove(key)

    // Manually dispatch a storage event to notify other hooks in the same tab
    window.dispatchEvent(
      new StorageEvent('storage', {
        key,
        oldValue: JSON.stringify(prev),
        newValue: JSON.stringify(storage.defaults[key]),
        storageArea: localStorage,
      }),
    )
  }, [key])

  return [value, setValue, remove]
}

export default useStorage
